import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { usePageTransition } from '../../utils/pageTransitions';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import ArticleCard from './components/ArticleCard';
import ArticleFilters from './components/ArticleFilters';
import { contentService } from '../../services/contentService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const NewsArticlesHub = () => {
  const { variants, transition } = usePageTransition();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('date');

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchArticles();
  }, []);

  useEffect(() => {
    filterAndSortArticles();
  }, [articles, searchQuery, selectedCategory, sortBy]);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const data = await contentService?.getAll();
      // Show all published content (blog posts + announcements), exclude pages
      const publishedArticles = data?.filter(
        (item) =>
          item?.status === 'published' &&
          item?.type !== 'page'
      ) || [];
      setArticles(publishedArticles);
      setFilteredArticles(publishedArticles);
    } catch (err) {
      console.error('Error fetching articles:', err);
      setError('Impossible de charger les articles');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortArticles = () => {
    let filtered = [...articles];

    if (searchQuery) {
      filtered = filtered?.filter(
        (article) =>
          article?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
          article?.excerpt?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered?.filter(
        (article) => article?.type === selectedCategory
      );
    }

    if (sortBy === 'date') {
      filtered?.sort((a, b) => new Date(b?.publishedAt || b?.createdAt) - new Date(a?.publishedAt || a?.createdAt));
    } else if (sortBy === 'title') {
      filtered?.sort((a, b) => a?.title?.localeCompare(b?.title));
    }

    setFilteredArticles(filtered);
  };

  const handleReadMore = (article) => {
    navigate(`/news-articles-hub/${article?.id}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date?.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <>
      <Helmet>
        <title>Actualités et Blog - ORAZEK Investment</title>
        <meta name="description" content="Restez informé des dernières nouvelles sur le business, l'immobilier et la technologie en RDC. Articles, analyses et conseils d'experts." />
      </Helmet>
      <Header />
      <motion.main
        className="flex-1 bg-white dark:bg-slate-900"
        initial="initial"
        animate="animate"
        exit="exit"
        variants={variants}
        transition={transition}
      >
        {/* Hero Section */}
        <section className="relative py-20 lg:py-32 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 overflow-hidden">
          {/* Noise texture */}
          <div className="absolute inset-0 opacity-10 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] brightness-100 contrast-150 mix-blend-overlay"></div>

          {/* Animated blobs */}
          <motion.div
            className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full bg-blue-500/15 blur-[150px]"
            animate={{ scale: [1, 1.15, 1], opacity: [0.15, 0.25, 0.15] }}
            transition={{ duration: 10, repeat: Infinity }}
          />
          <motion.div
            className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-amber-500/10 blur-[120px]"
            animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }}
            transition={{ duration: 8, repeat: Infinity, delay: 2 }}
          />

          <div className="relative w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/15 px-5 py-2.5 rounded-full mb-6"
              >
                <Icon name="Newspaper" size={18} className="text-blue-400" />
                <span className="text-sm font-medium text-blue-200">Actualités ORAZEK</span>
              </motion.div>

              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6" style={{ fontFamily: 'var(--font-headline)' }}>
                Centre d'{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-amber-400">
                  Actualités
                </span>
              </h1>
              <p className="text-lg md:text-xl text-blue-100/70 max-w-3xl mx-auto mb-10">
                Découvrez les dernières nouvelles, insights et mises à jour de l'équipe ORAZEK Investment
              </p>

              {/* Stats */}
              <div className="flex flex-wrap justify-center gap-8">
                {[
                  { icon: 'FileText', label: `${articles?.length || 0} Articles`, desc: 'Publiés' },
                  { icon: 'Eye', label: 'Insights', desc: 'Business & Tech' },
                  { icon: 'TrendingUp', label: 'Analyses', desc: 'Marché RDC' }
                ].map((stat, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + i * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-10 h-10 bg-white/10 backdrop-blur-md border border-white/15 rounded-xl flex items-center justify-center">
                      <Icon name={stat.icon} size={18} className="text-blue-400" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-semibold text-white">{stat.label}</p>
                      <p className="text-xs text-blue-200/60">{stat.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Filters Section */}
        <section className="bg-white/80 dark:bg-slate-800/60 backdrop-blur-xl border-y border-slate-200 dark:border-white/10 sticky top-0 z-40">
          <div className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16 py-4">
            <ArticleFilters
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
              sortBy={sortBy}
              onSortChange={setSortBy}
              totalResults={filteredArticles?.length}
            />
          </div>
        </section>

        {/* Content Section */}
        <section className="py-12 lg:py-20 bg-white dark:bg-slate-900">
          <div className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16">
            {loading ? (
              <div className="flex items-center justify-center py-32">
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-2 border-blue-500 border-t-transparent mb-4" />
                  <p className="text-slate-500 dark:text-slate-400">Chargement des articles...</p>
                </div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-32">
                <div className="text-center">
                  <div className="w-20 h-20 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Icon name="AlertCircle" size={36} className="text-red-400" />
                  </div>
                  <p className="text-red-400 text-lg mb-2">{error}</p>
                  <p className="text-slate-500 text-sm mb-6">Veuillez réessayer ultérieurement</p>
                  <Button
                    onClick={fetchArticles}
                    iconName="RefreshCw"
                    iconPosition="left"
                    className="bg-blue-600 hover:bg-blue-500 text-white border-none rounded-xl px-6 py-2.5"
                  >
                    Réessayer
                  </Button>
                </div>
              </div>
            ) : filteredArticles?.length === 0 ? (
              <div className="flex items-center justify-center py-32">
                <div className="text-center">
                  <div className="w-20 h-20 bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-white/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Icon name="FileText" size={36} className="text-slate-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">Aucun article trouvé</h3>
                  <p className="text-slate-600 dark:text-slate-400 mb-6">Essayez de modifier vos critères de recherche</p>
                  <Button
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory('all');
                    }}
                    iconName="RotateCcw"
                    iconPosition="left"
                    className="border-slate-300 dark:border-white/20 text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-white/5 rounded-xl px-6 py-2.5"
                    variant="outline"
                  >
                    Réinitialiser les filtres
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                {/* Grid Header */}
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-500/10 border border-blue-500/20 rounded-lg flex items-center justify-center">
                      <Icon name="Grid3x3" size={16} className="text-blue-400" />
                    </div>
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                      {searchQuery || selectedCategory !== 'all' ? 'Résultats' : 'Tous les articles'}
                    </h2>
                  </div>
                  <span className="text-sm text-slate-500 dark:text-slate-400">
                    {filteredArticles?.length} article{filteredArticles?.length !== 1 ? 's' : ''}
                  </span>
                </div>

                {/* Uniform Articles Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredArticles?.map((article, index) => (
                    <ArticleCard
                      key={article?.id}
                      article={article}
                      index={index}
                      onReadMore={handleReadMore}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Newsletter CTA Section */}
        <section className="relative py-20 lg:py-28 bg-white dark:bg-slate-900 overflow-hidden">
          <div className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16">
            <div className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 rounded-[2.5rem] p-10 md:p-16 overflow-hidden">
              {/* CTA blobs */}
              <motion.div
                className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full bg-white/5 blur-3xl"
                animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
                transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
              />
              <motion.div
                className="absolute bottom-0 left-0 w-[300px] h-[300px] rounded-full bg-amber-500/10 blur-3xl"
                animate={{ scale: [1.2, 1, 1.2], opacity: [0.2, 0.4, 0.2] }}
                transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
              />

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative text-center"
              >
                <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Icon name="Mail" size={32} className="text-white" />
                </div>
                <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-headline)' }}>
                  Restez informé
                </h2>
                <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto">
                  Abonnez-vous à notre newsletter pour recevoir les dernières actualités et analyses du marché congolais directement dans votre boîte mail
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-lg mx-auto">
                  <input
                    type="email"
                    placeholder="Votre adresse email..."
                    className="flex-1 bg-white/10 border border-white/20 backdrop-blur-sm rounded-xl px-5 py-3.5 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/30 transition-all"
                  />
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button
                      variant="default"
                      className="bg-white text-blue-700 hover:bg-white/90 shadow-2xl border-none rounded-xl px-8 py-3.5 font-semibold whitespace-nowrap"
                      iconName="Send"
                      iconPosition="right"
                    >
                      S'abonner
                    </Button>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </motion.main>
      <Footer />
    </>
  );
};

export default NewsArticlesHub;
